import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, ButtonGroup, Card, CardBody, Col, FormFeedback, FormGroup, Input, Label, Row } from "reactstrap";
import { DesktopContainer } from "../layouts";
import { api, helpers } from "../utils";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

export default function InvoiceSettings(props) {
   const location = useLocation();
   const navigate = useNavigate();
   const [customerName, setCustomerName] = useState("");
   const [editIndex, setEditIndex] = useState(null);
   const [emailCopy, setEmailCopy] = useState(null);
   const [emailInvalid, setEmailInvalid] = useState(false);
   const [emails, setEmails] = useState([]);
   const [emailSelectCopy, setEmailSelectCopy] = useState(null);
   const [flags, setFlags] = useState([]);
   const [possibleEmails, setPossibleEmails] = useState([]);
   // True when the initial load failed. We deliberately do NOT fall back to default flags here:
   // each toggle/save posts the whole flags object (incl. the order flags this page doesn't show),
   // so editing on a failed load would silently overwrite real settings. Block edits instead.
   const [loadError, setLoadError] = useState(false);

   useEffect(() => {
      if (location && location.state && location.state.customerId) {
         api
            .fetch(`Order/LoadInvoiceSettings`, {
               customerId: location.state.customerId,
               orderId: location.state.invoiceNumber,
            })
            .then((response) => {
               if (response.data.success) {
                  setFlags(response.data.flags);
                  let emailList = [];
                  if (_.trim(response.data.customerInfo.invoiceRecipients)) {
                     emailList = _.split(
                        response.data.customerInfo.invoiceRecipients,
                        /[;: ]/
                     );
                  }
                  setEmails(emailList);
                  setCustomerName(response.data.customerInfo.customerName);
                  let possibleEmails = [];
                  for (let i = 0; i < response.data.emails.length; i++) {
                     let emailDto = response.data.emails[i];
                     if (
                        !_.isEmpty(emailDto?.contactEmail) &&
                        !_.includes(possibleEmails, emailDto?.contactEmail)
                     )
                        handleMultiple(emailDto?.contactEmail, possibleEmails);
                     if (
                        !_.isEmpty(emailDto?.toEmail) &&
                        !_.includes(possibleEmails, emailDto?.toEmail)
                     )
                        handleMultiple(emailDto?.toEmail, possibleEmails);
                     if (
                        !_.isEmpty(emailDto?.ccEmail) &&
                        !_.includes(possibleEmails, emailDto?.ccEmail)
                     )
                        handleMultiple(emailDto?.ccEmail, possibleEmails);
                     if (
                        !_.isEmpty(emailDto?.bccEmail) &&
                        !_.includes(possibleEmails, emailDto?.bccEmail)
                     )
                        handleMultiple(emailDto?.bccEmail, possibleEmails);
                  }
                  let uniqueArray = _.uniqWith(possibleEmails, _.isEqual);
                  setPossibleEmails(uniqueArray);
               } else {
                  toast.error(response.data.message || "Couldn't load invoice settings. Please go back and try again.");
                  setLoadError(true);
               }
            })
            .catch((e) => {
               console.error(e);
               toast.error("Couldn't load invoice settings. Please go back and try again.");
               setLoadError(true);
            });
      }
   }, [location]);

   function allowEmailEdit(index) {
      setEmailCopy(emails[index]);
      setEditIndex(index);
   }

   function cancelEmailEdit() {
      setEmailCopy(null);
      setEditIndex(null);
   }

   function deleteEmail(index) {
      const emailsCopy = _.slice(emails);
      _.pullAt(emailsCopy, index);
      setEmails(emailsCopy);
   }

   function handleEmailSave() {
      if (loadError) {
         toast.error("Settings didn't load correctly — please go back and reopen before saving.");
         return;
      }
      const payload = {
         invoiceRecipients: _.join(emails, ";"),
         orderId:
            location.state.orderId === "N/A"
               ? location.state.invoiceNumber
               : location.state.orderId,
      };

      api
         .post("Order/SaveInvoiceDl", payload)
         .then((r) => {
            if (r.data.success) {
               toast.success("Invoice list updated!");
               navigate("/process-invoices");
            } else {
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   function handleFlagChange(flagName) {
      if (loadError) {
         toast.error("Settings didn't load correctly — please go back and reopen before changing.");
         return;
      }
      const flagsCopy = _.assign({}, flags);
      flagsCopy[flagName] = !flagsCopy[flagName];
      setFlags(flagsCopy);

      api
         .post(`Customer/SaveInvoiceOrderFlags`, flagsCopy)
         .then((response) => {
            if (response.data.success) {
               toast.success("Email Settings updated");
            } else {
               toast.error("Error saving Email Settings");
            }
         })
         .catch(api.catchHandler);
   }

   function handleMultiple(content, list) {
      if (
         _.includes(content, ";") ||
         _.includes(content, ":") ||
         _.includes(content, " ")
      ) {
         const contacts = _.split(content, /[;: ]/);
         _.forEach(contacts, function (s) {
            if (_.trim(s)) {
               list.push({ label: s, value: s });
            }
         });
      } else {
         list.push({ label: content, value: content });
      }
   }

   function tryAddEmail(email) {
      if (helpers.emailIsValid(email)) {
         setEmailInvalid(false);
         const emailListCopy = _.slice(emails);

         if (editIndex === null) {
            emailListCopy.push(email);
            setEmailCopy(null);
            setEmailSelectCopy(null);
         } else {
            emailListCopy[editIndex] = email;
            setEditIndex(null);
            setEmailCopy(null);
         }
         setEmails(emailListCopy);
      } else {
         setEmailInvalid(true);
      }
   }

   return (
      <DesktopContainer screenName="Invoice Email Settings">
         <Card>
            <CardBody>
               <div className="h5 fw-bold" children={`Invoice #: ${location.state.invoiceNumber}`} />
               <div className="h5 fw-bold" children={`Original Order #: ${location.state.orderId || ""}`} />
               <div className="h5 fw-bold" children={`Customer #: ${customerName}`} />
               <hr />
               <Row className="mt-3">
                  <Col xs={4}>
                     <h5 className="fw-bold">
                        Invoices will be sent <br /> via the following <br /> method(s)
                     </h5>
                  </Col>
                  <Col xs={8}>
                     <FormGroup check inline className="me-5">
                        <Label check>
                           <Input
                              type="checkbox"
                              name="emailInvoice"
                              checked={(flags && flags.emailInvoice) || false}
                              onChange={(e) => handleFlagChange(e.target.name)}
                              disabled={loadError}
                           />
                           Email
                        </Label>
                     </FormGroup>
                     <FormGroup check inline className="me-5">
                        <Label check>
                           <Input
                              type="checkbox"
                              name="faxInvoice"
                              checked={(flags && flags.faxInvoice) || false}
                              onChange={(e) => handleFlagChange(e.target.name)}
                              disabled={loadError}
                           />
                           Fax
                        </Label>
                     </FormGroup>
                     <FormGroup check inline className="me-5">
                        <Label check>
                           <Input
                              type="checkbox"
                              name="postInvoice"
                              checked={(flags && flags.postInvoice) || false}
                              onChange={(e) => handleFlagChange(e.target.name)}
                              disabled={loadError}
                           />
                           Postal Mail
                        </Label>
                     </FormGroup>
                  </Col>
               </Row>
               <hr />

               <FormGroup row>
                  <Col xs={2}>
                     <h5 className="fw-bold">
                        Manually
                        Add a CC
                        Recipient:{" "}
                     </h5>
                  </Col>
                  <Col xs={8}>
                     <Input
                        id="newEmail"
                        value={editIndex === null ? emailCopy || "" : ""}
                        onChange={(e) => {
                           setEmailCopy(e.target.value);
                           if (!e.target.value) setEmailInvalid(false);
                        }}
                        invalid={emailInvalid && editIndex === null && emailCopy}
                        className={editIndex === null ? "" : "readonly-input"}
                        readOnly={editIndex !== null}
                     />
                     {emailInvalid && emailCopy && (
                        <FormFeedback children="Invalid email. Please correct and try again." />
                     )}
                  </Col>
                  <Col xs={2}>
                     <Button
                        className="shredButton w-100"
                        children="Add Email"
                        onClick={() => tryAddEmail(emailCopy)}
                        disabled={emailCopy === null || _.isEmpty(emailCopy)}
                     />
                  </Col>
               </FormGroup>
               {_.map(emails, (email, index) => {
                  return (
                     <Row key={index}>
                        <Col xs={{ size: 8, offset: 2 }}>
                           <FormGroup>
                              <Input
                                 value={editIndex === index ? emailCopy : email}
                                 onChange={(e) => setEmailCopy(e.target.value)}
                                 invalid={emailInvalid && editIndex === index}
                                 className={editIndex === index ? "" : "readonly-input"}
                                 readOnly={editIndex !== index}
                              />
                              {emailInvalid && editIndex === index && (
                                 <FormFeedback
                                    visible={emailInvalid}
                                    children="Invalid email. Please correct and try again."
                                 />
                              )}
                           </FormGroup>
                        </Col>
                        <Col className="text-center" xs={2}>
                           <ButtonGroup>
                              <Button
                                 className="icon-button me-3"
                                 color={editIndex === index ? "success" : "info"}
                                 onClick={
                                    editIndex === index
                                       ? () => tryAddEmail(emailCopy)
                                       : () => allowEmailEdit(index)
                                 }
                                 disabled={editIndex !== null && editIndex !== index}
                              >
                                 <FontAwesomeIcon className="whiteIcon" icon={editIndex === index ? "check" : "edit"} />
                              </Button>
                              <Button
                                 className="icon-button"
                                 color={editIndex === index ? "secondary" : "danger"}
                                 onClick={
                                    editIndex === index
                                       ? () => cancelEmailEdit()
                                       : () => deleteEmail(index)
                                 }
                                 title={
                                    editIndex === index ? "Discard Changes" : "Delete Email"
                                 }
                                 disabled={editIndex !== null && editIndex !== index}
                              >
                                 <FontAwesomeIcon className="whiteIcon" icon={editIndex === index ? "times" : "trash"} />
                              </Button>
                           </ButtonGroup>
                        </Col>
                     </Row>
                  );
               })}
               <FormGroup row>
                  <Col xs={2}>
                     <h5 className="fw-bold">
                        Additional
                        Available CC
                        Recipients:
                     </h5>
                  </Col>
                  <Col xs={8}>
                     <Select
                        id="newEmail"
                        onChange={(e) => { setEmailSelectCopy(e.value); }}
                        value={_.find(possibleEmails, { value: emailSelectCopy }) || ""}
                        options={possibleEmails}
                        classNamePrefix="serviceThreadSelect"
                     />
                  </Col>
                  <Col xs={2}>
                     <Button
                        className="shredButton w-100"
                        children="Add Email"
                        onClick={() => tryAddEmail(emailSelectCopy)}
                        disabled={emailSelectCopy === null}
                     />
                  </Col>
               </FormGroup>
               <FormGroup row>
                  <Col xs={{ size: 2, offset: 10 }}>
                     <Button
                        className="shredButton"
                        style={{ width: "100%" }}
                        children="Save Changes"
                        onClick={handleEmailSave}
                     />
                  </Col>
               </FormGroup>
            </CardBody>
         </Card>
      </DesktopContainer>
   );
}
